import { Skeleton } from '@pharmaconnect/ui/web'
import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { AuthGuard } from './components/AuthGuard'
import { HomeRedirect, RoleGuard } from './components/RoleGuard'
import { DashboardLayout } from './layouts/DashboardLayout'
// Pages chargées au premier render (auth + landing) restent eager pour
// minimiser le temps avant first paint sur l'écran de login.
import { DashboardPage } from './pages/DashboardPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { LoginPage } from './pages/LoginPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'

// Vague 15 : code-split — chaque page authentifiée part dans son propre
// chunk, téléchargé à la navigation. Réduit le bundle initial des owners
// d'environ 1 200kB → 250kB.
const AccountingPage = lazy(() =>
  import('./pages/AccountingPage').then((m) => ({ default: m.AccountingPage })),
)
const AccountingExportsPage = lazy(() =>
  import('./pages/AccountingExportsPage').then((m) => ({
    default: m.AccountingExportsPage,
  })),
)
const AdminErrorsPage = lazy(() =>
  import('./pages/AdminErrorsPage').then((m) => ({ default: m.AdminErrorsPage })),
)
const AdminNotificationsDlqPage = lazy(() =>
  import('./pages/AdminNotificationsDlqPage').then((m) => ({
    default: m.AdminNotificationsDlqPage,
  })),
)
const AdminBroadcastOwnersPage = lazy(() =>
  import('./pages/AdminBroadcastOwnersPage').then((m) => ({
    default: m.AdminBroadcastOwnersPage,
  })),
)
const AdminPharmaciesOverviewPage = lazy(() =>
  import('./pages/AdminPharmaciesOverviewPage').then((m) => ({
    default: m.AdminPharmaciesOverviewPage,
  })),
)
const AdminDashboardPage = lazy(() =>
  import('./pages/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })),
)
const AdminPharmacovigilancePage = lazy(() =>
  import('./pages/AdminPharmacovigilancePage').then((m) => ({
    default: m.AdminPharmacovigilancePage,
  })),
)
const AdminDciStatsPage = lazy(() =>
  import('./pages/AdminDciStatsPage').then((m) => ({ default: m.AdminDciStatsPage })),
)
const AdminSuspectPrescriptionsPage = lazy(() =>
  import('./pages/AdminSuspectPrescriptionsPage').then((m) => ({
    default: m.AdminSuspectPrescriptionsPage,
  })),
)
const AdminDutySchedulePage = lazy(() =>
  import('./pages/AdminDutySchedulePage').then((m) => ({
    default: m.AdminDutySchedulePage,
  })),
)
const AdminRecommendedVaccinesPage = lazy(() =>
  import('./pages/AdminRecommendedVaccinesPage').then((m) => ({
    default: m.AdminRecommendedVaccinesPage,
  })),
)
const AdminVaccineCoveragePage = lazy(() =>
  import('./pages/AdminVaccineCoveragePage').then((m) => ({
    default: m.AdminVaccineCoveragePage,
  })),
)
const AdminBloodDonationsPage = lazy(() =>
  import('./pages/AdminBloodDonationsPage').then((m) => ({
    default: m.AdminBloodDonationsPage,
  })),
)
const AdminBloodStatsPage = lazy(() =>
  import('./pages/AdminBloodStatsPage').then((m) => ({
    default: m.AdminBloodStatsPage,
  })),
)
const AdminSecurityPage = lazy(() =>
  import('./pages/AdminSecurityPage').then((m) => ({ default: m.AdminSecurityPage })),
)
const AdminTrainingPage = lazy(() =>
  import('./pages/AdminTrainingPage').then((m) => ({ default: m.AdminTrainingPage })),
)
const AdminWebhooksPage = lazy(() =>
  import('./pages/AdminWebhooksPage').then((m) => ({ default: m.AdminWebhooksPage })),
)
const AnalyticsPage = lazy(() =>
  import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })),
)
const AnomaliesPage = lazy(() =>
  import('./pages/AnomaliesPage').then((m) => ({ default: m.AnomaliesPage })),
)
const AdherenceAlertsPage = lazy(() =>
  import('./pages/AdherenceAlertsPage').then((m) => ({ default: m.AdherenceAlertsPage })),
)
const AppointmentsPage = lazy(() =>
  import('./pages/AppointmentsPage').then((m) => ({ default: m.AppointmentsPage })),
)
const PharmacyVaccinesPage = lazy(() =>
  import('./pages/PharmacyVaccinesPage').then((m) => ({
    default: m.PharmacyVaccinesPage,
  })),
)
const PharmacyVaccineStatsPage = lazy(() =>
  import('./pages/PharmacyVaccineStatsPage').then((m) => ({
    default: m.PharmacyVaccineStatsPage,
  })),
)
const AssistantPage = lazy(() =>
  import('./pages/AssistantPage').then((m) => ({ default: m.AssistantPage })),
)
const AuditLogPage = lazy(() =>
  import('./pages/AuditLogPage').then((m) => ({ default: m.AuditLogPage })),
)
const BackupsPage = lazy(() =>
  import('./pages/BackupsPage').then((m) => ({ default: m.BackupsPage })),
)
const CashClosurePage = lazy(() =>
  import('./pages/CashClosurePage').then((m) => ({ default: m.CashClosurePage })),
)
const CashierHistoryPage = lazy(() =>
  import('./pages/CashierHistoryPage').then((m) => ({ default: m.CashierHistoryPage })),
)
const CashierPage = lazy(() =>
  import('./pages/CashierPage').then((m) => ({ default: m.CashierPage })),
)
const CashiersPage = lazy(() =>
  import('./pages/CashiersPage').then((m) => ({ default: m.CashiersPage })),
)
const CustomerCreditsPage = lazy(() =>
  import('./pages/CustomerCreditsPage').then((m) => ({ default: m.CustomerCreditsPage })),
)
const DeliveriesPage = lazy(() =>
  import('./pages/DeliveriesPage').then((m) => ({ default: m.DeliveriesPage })),
)
const DoctorPatientsPage = lazy(() =>
  import('./pages/DoctorPatientsPage').then((m) => ({ default: m.DoctorPatientsPage })),
)
const DoctorPregnanciesPage = lazy(() =>
  import('./pages/DoctorPregnanciesPage').then((m) => ({ default: m.DoctorPregnanciesPage })),
)
const DoctorPrescribePage = lazy(() =>
  import('./pages/DoctorPrescribePage').then((m) => ({ default: m.DoctorPrescribePage })),
)
const DoctorStatsPage = lazy(() =>
  import('./pages/DoctorStatsPage').then((m) => ({ default: m.DoctorStatsPage })),
)
const DoctorTemplatesPage = lazy(() =>
  import('./pages/DoctorTemplatesPage').then((m) => ({ default: m.DoctorTemplatesPage })),
)
const DoctorsPage = lazy(() =>
  import('./pages/DoctorsPage').then((m) => ({ default: m.DoctorsPage })),
)
const DriversPage = lazy(() =>
  import('./pages/DriversPage').then((m) => ({ default: m.DriversPage })),
)
const EmployeesPage = lazy(() =>
  import('./pages/EmployeesPage').then((m) => ({ default: m.EmployeesPage })),
)
const LeavePage = lazy(() =>
  import('./pages/LeavePage').then((m) => ({ default: m.LeavePage })),
)
const MyLeavePage = lazy(() =>
  import('./pages/MyLeavePage').then((m) => ({ default: m.MyLeavePage })),
)
const FormationPage = lazy(() =>
  import('./pages/FormationPage').then((m) => ({ default: m.FormationPage })),
)
const InboxPage = lazy(() =>
  import('./pages/InboxPage').then((m) => ({ default: m.InboxPage })),
)
const AdminInsuranceProvidersPage = lazy(() =>
  import('./pages/AdminInsuranceProvidersPage').then((m) => ({
    default: m.AdminInsuranceProvidersPage,
  })),
)
const AdminDciCatalogPage = lazy(() =>
  import('./pages/AdminDciCatalogPage').then((m) => ({
    default: m.AdminDciCatalogPage,
  })),
)
const AdminDrugInteractionsPage = lazy(() =>
  import('./pages/AdminDrugInteractionsPage').then((m) => ({
    default: m.AdminDrugInteractionsPage,
  })),
)
const AdminQuizPage = lazy(() =>
  import('./pages/AdminQuizPage').then((m) => ({ default: m.AdminQuizPage })),
)
const AdminRecallsPage = lazy(() =>
  import('./pages/AdminRecallsPage').then((m) => ({ default: m.AdminRecallsPage })),
)
const BatchesPage = lazy(() =>
  import('./pages/BatchesPage').then((m) => ({ default: m.BatchesPage })),
)
const InsuranceClaimsPage = lazy(() =>
  import('./pages/InsuranceClaimsPage').then((m) => ({ default: m.InsuranceClaimsPage })),
)
const KioskAdminPage = lazy(() =>
  import('./pages/KioskAdminPage').then((m) => ({ default: m.KioskAdminPage })),
)
const KioskPublicPage = lazy(() =>
  import('./pages/KioskPublicPage').then((m) => ({ default: m.KioskPublicPage })),
)
const NpsPublicPage = lazy(() =>
  import('./pages/NpsPublicPage').then((m) => ({ default: m.NpsPublicPage })),
)
const NpsDashboardPage = lazy(() =>
  import('./pages/NpsDashboardPage').then((m) => ({ default: m.NpsDashboardPage })),
)
const InventoryPage = lazy(() =>
  import('./pages/InventoryPage').then((m) => ({ default: m.InventoryPage })),
)
const LoyaltyPage = lazy(() =>
  import('./pages/LoyaltyPage').then((m) => ({ default: m.LoyaltyPage })),
)
const LoyaltyRewardsPage = lazy(() =>
  import('./pages/LoyaltyRewardsPage').then((m) => ({ default: m.LoyaltyRewardsPage })),
)
const NotificationsPage = lazy(() =>
  import('./pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
)
const PharmacistNotificationsPage = lazy(() =>
  import('./pages/PharmacistNotificationsPage').then((m) => ({
    default: m.PharmacistNotificationsPage,
  })),
)
const OnboardPharmacyPage = lazy(() =>
  import('./pages/OnboardPharmacyPage').then((m) => ({ default: m.OnboardPharmacyPage })),
)
const PaymentsPage = lazy(() =>
  import('./pages/PaymentsPage').then((m) => ({ default: m.PaymentsPage })),
)
const PrescriptionsPage = lazy(() =>
  import('./pages/PrescriptionsPage').then((m) => ({ default: m.PrescriptionsPage })),
)
const ProformaInvoicesPage = lazy(() =>
  import('./pages/ProformaInvoicesPage').then((m) => ({ default: m.ProformaInvoicesPage })),
)
const QuarterlyReportsPage = lazy(() =>
  import('./pages/QuarterlyReportsPage').then((m) => ({
    default: m.QuarterlyReportsPage,
  })),
)
const AnnualReportsPage = lazy(() =>
  import('./pages/AnnualReportsPage').then((m) => ({ default: m.AnnualReportsPage })),
)
const RatingsPage = lazy(() =>
  import('./pages/RatingsPage').then((m) => ({ default: m.RatingsPage })),
)
const SmsCampaignsPage = lazy(() =>
  import('./pages/SmsCampaignsPage').then((m) => ({ default: m.SmsCampaignsPage })),
)
const PublicHealthAlertsPage = lazy(() =>
  import('./pages/PublicHealthAlertsPage').then((m) => ({ default: m.PublicHealthAlertsPage })),
)
const PurchaseOrdersPage = lazy(() =>
  import('./pages/PurchaseOrdersPage').then((m) => ({ default: m.PurchaseOrdersPage })),
)
const BulkPricesPage = lazy(() =>
  import('./pages/BulkPricesPage').then((m) => ({ default: m.BulkPricesPage })),
)
const DisposalsPage = lazy(() =>
  import('./pages/DisposalsPage').then((m) => ({ default: m.DisposalsPage })),
)
const DynamicThresholdsPage = lazy(() =>
  import('./pages/DynamicThresholdsPage').then((m) => ({
    default: m.DynamicThresholdsPage,
  })),
)
const DynamicPricingPage = lazy(() =>
  import('./pages/DynamicPricingPage').then((m) => ({ default: m.DynamicPricingPage })),
)
const ReapproPage = lazy(() =>
  import('./pages/ReapproPage').then((m) => ({ default: m.ReapproPage })),
)
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const StockPage = lazy(() =>
  import('./pages/StockPage').then((m) => ({ default: m.StockPage })),
)
const SuppliersPage = lazy(() =>
  import('./pages/SuppliersPage').then((m) => ({ default: m.SuppliersPage })),
)
const SupplierReturnsPage = lazy(() =>
  import('./pages/SupplierReturnsPage').then((m) => ({ default: m.SupplierReturnsPage })),
)
const PromotionsPage = lazy(() =>
  import('./pages/PromotionsPage').then((m) => ({ default: m.PromotionsPage })),
)
const MarketplacePage = lazy(() =>
  import('./pages/MarketplacePage').then((m) => ({ default: m.MarketplacePage })),
)
const TvaPage = lazy(() => import('./pages/TvaPage').then((m) => ({ default: m.TvaPage })))

const PageFallback = () => (
  <div className="p-6">
    <Skeleton height="h-8" />
  </div>
)

const lazyRoute = (
  Component: React.LazyExoticComponent<React.ComponentType<unknown>>,
) => (
  <Suspense fallback={<PageFallback />}>
    <Component />
  </Suspense>
)

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* V60 — Kiosque public (no auth, accès via token) */}
      <Route path="/k/:token" element={lazyRoute(KioskPublicPage)} />
      {/* V90 — NPS public page (token in URL, no auth) */}
      <Route path="/nps/:token" element={lazyRoute(NpsPublicPage)} />
      <Route element={<AuthGuard />}>
        <Route element={<DashboardLayout />}>
          <Route
            path="/"
            element={
              <HomeRedirect>
                <DashboardPage />
              </HomeRedirect>
            }
          />
          <Route path="/caisse" element={lazyRoute(CashierPage)} />
          <Route path="/caisse/historique" element={lazyRoute(CashierHistoryPage)} />
          <Route path="/cloture" element={lazyRoute(CashClosurePage)} />
          <Route path="/credits" element={lazyRoute(CustomerCreditsPage)} />
          <Route path="/livraisons" element={lazyRoute(DeliveriesPage)} />
          <Route path="/formation" element={lazyRoute(FormationPage)} />
          <Route path="/mes-conges" element={lazyRoute(MyLeavePage)} />

          {/* Espaces réservés au personnel pharmacie hors caissier */}
          <Route element={<RoleGuard allow={['owner', 'manager', 'super_admin']} />}>
            <Route path="/assistant" element={lazyRoute(AssistantPage)} />
            <Route path="/inbox" element={lazyRoute(InboxPage)} />
            <Route path="/stocks" element={lazyRoute(StockPage)} />
            <Route path="/inventaire" element={lazyRoute(InventoryPage)} />
            <Route path="/ordonnances" element={lazyRoute(PrescriptionsPage)} />
            <Route path="/paiements" element={lazyRoute(PaymentsPage)} />
            <Route path="/comptabilite" element={lazyRoute(AccountingPage)} />
            <Route path="/exports-comptables" element={lazyRoute(AccountingExportsPage)} />
            <Route path="/notifications" element={lazyRoute(NotificationsPage)} />
            <Route path="/notifications-pharmacien" element={lazyRoute(PharmacistNotificationsPage)} />
            <Route path="/fidelite" element={lazyRoute(LoyaltyPage)} />
            <Route path="/recompenses" element={lazyRoute(LoyaltyRewardsPage)} />
            <Route path="/analytics" element={lazyRoute(AnalyticsPage)} />
            <Route path="/anomalies" element={lazyRoute(AnomaliesPage)} />
            <Route path="/adherence-alerts" element={lazyRoute(AdherenceAlertsPage)} />
            <Route path="/devis" element={lazyRoute(ProformaInvoicesPage)} />
            <Route path="/notation" element={lazyRoute(RatingsPage)} />
            <Route path="/nps" element={lazyRoute(NpsDashboardPage)} />
            <Route path="/rdv" element={lazyRoute(AppointmentsPage)} />
            <Route path="/vaccins-patients" element={lazyRoute(PharmacyVaccinesPage)} />
            <Route path="/stats-vaccins" element={lazyRoute(PharmacyVaccineStatsPage)} />
            <Route path="/kiosque-admin" element={lazyRoute(KioskAdminPage)} />
            <Route path="/fournisseurs" element={lazyRoute(SuppliersPage)} />
            <Route path="/retours-fournisseurs" element={lazyRoute(SupplierReturnsPage)} />
            <Route path="/promotions" element={lazyRoute(PromotionsPage)} />
            <Route path="/marketplace" element={lazyRoute(MarketplacePage)} />
            <Route path="/commandes-achat" element={lazyRoute(PurchaseOrdersPage)} />
            <Route path="/reappro" element={lazyRoute(ReapproPage)} />
            <Route path="/seuils-dynamiques" element={lazyRoute(DynamicThresholdsPage)} />
            <Route path="/pricing-peremption" element={lazyRoute(DynamicPricingPage)} />
            <Route path="/destructions" element={lazyRoute(DisposalsPage)} />
            <Route path="/tarifs-bulk" element={lazyRoute(BulkPricesPage)} />
            <Route path="/rapports" element={lazyRoute(QuarterlyReportsPage)} />
            <Route path="/bilans-annuels" element={lazyRoute(AnnualReportsPage)} />
            <Route path="/sauvegardes" element={lazyRoute(BackupsPage)} />
            <Route path="/tiers-payant" element={lazyRoute(InsuranceClaimsPage)} />
            <Route path="/lots" element={lazyRoute(BatchesPage)} />
            <Route path="/tva" element={lazyRoute(TvaPage)} />
          </Route>

          <Route element={<RoleGuard allow={['owner', 'super_admin']} />}>
            <Route path="/rh" element={lazyRoute(EmployeesPage)} />
            <Route path="/conges" element={lazyRoute(LeavePage)} />
            <Route path="/campagnes" element={lazyRoute(SmsCampaignsPage)} />
            <Route path="/audit" element={lazyRoute(AuditLogPage)} />
            <Route path="/caissiers" element={lazyRoute(CashiersPage)} />
          </Route>

          <Route element={<RoleGuard allow={['doctor']} />}>
            <Route path="/prescrire" element={lazyRoute(DoctorPrescribePage)} />
            <Route path="/mes-patients" element={lazyRoute(DoctorPatientsPage)} />
            <Route path="/mes-templates" element={lazyRoute(DoctorTemplatesPage)} />
            <Route path="/mes-grossesses" element={lazyRoute(DoctorPregnanciesPage)} />
            <Route path="/statistiques-medecin" element={lazyRoute(DoctorStatsPage)} />
          </Route>

          <Route element={<RoleGuard allow={['super_admin']} />}>
            <Route path="/medecins" element={lazyRoute(DoctorsPage)} />
            <Route path="/livreurs" element={lazyRoute(DriversPage)} />
            <Route path="/alertes" element={lazyRoute(PublicHealthAlertsPage)} />
            <Route path="/pharmacovigilance" element={lazyRoute(AdminPharmacovigilancePage)} />
            <Route path="/admin/dci-stats" element={lazyRoute(AdminDciStatsPage)} />
            <Route path="/admin/suspect-prescriptions" element={lazyRoute(AdminSuspectPrescriptionsPage)} />
            <Route path="/admin/duty-schedule" element={lazyRoute(AdminDutySchedulePage)} />
            <Route path="/admin/vaccines" element={lazyRoute(AdminRecommendedVaccinesPage)} />
            <Route path="/admin/vaccine-coverage" element={lazyRoute(AdminVaccineCoveragePage)} />
            <Route path="/admin/blood-donations" element={lazyRoute(AdminBloodDonationsPage)} />
            <Route path="/admin/blood-stats" element={lazyRoute(AdminBloodStatsPage)} />
            <Route path="/admin/erreurs" element={lazyRoute(AdminErrorsPage)} />
            <Route path="/admin/notifications-dlq" element={lazyRoute(AdminNotificationsDlqPage)} />
            <Route path="/admin/broadcast-owners" element={lazyRoute(AdminBroadcastOwnersPage)} />
            <Route path="/admin" element={lazyRoute(AdminDashboardPage)} />
            <Route path="/admin/pharmacies" element={lazyRoute(AdminPharmaciesOverviewPage)} />
            <Route path="/admin/onboard-pharmacy" element={lazyRoute(OnboardPharmacyPage)} />
            <Route path="/admin/webhooks" element={lazyRoute(AdminWebhooksPage)} />
            <Route path="/admin/security" element={lazyRoute(AdminSecurityPage)} />
            <Route path="/admin/formation" element={lazyRoute(AdminTrainingPage)} />
            <Route
              path="/admin/assurances"
              element={lazyRoute(AdminInsuranceProvidersPage)}
            />
            <Route path="/admin/recalls" element={lazyRoute(AdminRecallsPage)} />
            <Route path="/admin/quiz" element={lazyRoute(AdminQuizPage)} />
            <Route path="/admin/dci-catalog" element={lazyRoute(AdminDciCatalogPage)} />
            <Route path="/admin/interactions" element={lazyRoute(AdminDrugInteractionsPage)} />
          </Route>

          <Route element={<RoleGuard allow={['owner', 'manager', 'super_admin', 'doctor']} />}>
            <Route path="/parametres" element={lazyRoute(SettingsPage)} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
